import { useEffect } from 'react'
import { trackInternalEvent } from '@rio/tracking'
import ReactDOM from 'react-dom'
import { Icon24 } from 'builder/components/Icon'
import { useI18n } from 'builder/hooks/useI18n'
import { SnackBar } from 'builder/components/SnackBarV2'
import { usePostRedirectUrl } from '../hooks/useGetRedirectUrl'

import Carousel from '../Carousel'
import { CourseCard } from '../CourseCard'
import { RawCourse } from '../types'
import { TRANSLATION } from '../utils'

import { useGetCourses } from '../hooks/useGetCourses'
import { CoursesSkeleton } from '../Skeleton'
import { Container, Header, LinkButton, Title, JobCoursesTitle } from './styles'

const CoursesList = (props: {
  courses: RawCourse[]
  saved: RawCourse[]
  redirectToWebo: (cmsgid?: string) => void
}) => {
  return (
    <Carousel>
      {props.courses.map((course, index) => (
        <CourseCard
          key={index}
          courseId={course.cmsgid}
          onClick={() => props.redirectToWebo(course.cmsgid)}
          provider={course.organisation.name}
          title={course.title}
          category={course.categories[0]}
          progress={course.suitability}
          description={course.introduction}
          weeks={course.duration_in_weeks}
          imageSrc={course.small_image}
          hoursPerWeek={course.hours_per_week}
          status="recomended"
          isSaved={props.saved.some(savedCourse => savedCourse.cmsgid === course.cmsgid)}
          logoSrc={course?.organisation?.organisation_image as string | undefined}
          rating={
            course.reviews?.average_score ? parseFloat(course.reviews.average_score) : undefined
          }
          reviewCount={course.reviews?.count ? parseInt(course.reviews.count, 10) : undefined}
          videoTrailer={course.video_trailer}
        />
      ))}
    </Carousel>
  )
}

const RecomendedCourses = (props: {
  data: RawCourse[]
  saved: RawCourse[]
  title?: React.ReactNode
}) => {
  const { i18n } = useI18n()
  const { mutateAndRedirect } = usePostRedirectUrl()

  const handleClick = (cmsgid?: string) => {
    trackInternalEvent('click_more_recommended_courses')
    mutateAndRedirect({ cmsgid })
  }

  useEffect(() => {
    trackInternalEvent('view_recommended_courses', { source: 'dashboard' })
  }, [])

  const data = Array.isArray(props.data) ? props.data : (Object.values(props.data) as RawCourse[])
  if (data.length === 0) return null

  return (
    <Container id="recomended-courses" data-testid="recomended-courses">
      <Header data-testid="recommended-courses-header">
        {props.title ?? <Title>{i18n.t(`${TRANSLATION}.recommended.title`)}</Title>}
        <LinkButton to="#" onClick={() => handleClick()} data-testid="see-all-courses-button">
          {i18n.t(`${TRANSLATION}.recommended.button`)} <Icon24.ChevronRight />
        </LinkButton>
      </Header>
      <CoursesList courses={data} saved={props.saved} redirectToWebo={handleClick} />
    </Container>
  )
}

const JobRecomendedCourses = () => {
  const { i18n } = useI18n()
  const { mutateAndRedirect } = usePostRedirectUrl()
  const { data, isLoading, error } = useGetCourses()

  const redirectToWebo = (cmsgid?: string) => {
    trackInternalEvent('click_more_recommended_courses')
    mutateAndRedirect({ cmsgid })
  }

  useEffect(() => {
    trackInternalEvent('view_recommended_courses', { source: 'job_search' })
  }, [])

  if (error) return null
  if (isLoading) return <CoursesSkeleton />
  if (!data?.jobTitle) return null

  return (
    <Container id="recomended-courses" gap="24px">
      <Header>
        <JobCoursesTitle>{i18n.t(`${TRANSLATION}.recommended.job_courses_title`)}</JobCoursesTitle>
        <LinkButton to="#" onClick={() => redirectToWebo()}>
          {i18n.t(`${TRANSLATION}.recommended.button`)} <Icon24.ChevronRight />
        </LinkButton>
      </Header>
      <CoursesList
        courses={data?.recommended}
        saved={data?.progression.saved ?? []}
        redirectToWebo={redirectToWebo}
      />
      {ReactDOM.createPortal(<SnackBar />, document.body)}
    </Container>
  )
}

const MyProfileRecomendedCourses = () => {
  const { i18n } = useI18n()
  const { mutateAndRedirect } = usePostRedirectUrl()
  const { data, isLoading, error } = useGetCourses()

  const redirectToWebo = (cmsgid?: string) => {
    trackInternalEvent('click_more_recommended_courses')
    mutateAndRedirect({ cmsgid })
  }

  useEffect(() => {
    trackInternalEvent('view_recommended_courses', { source: 'job_search' })
  }, [])

  if (error) return null
  if (isLoading) return <CoursesSkeleton />
  if (!data?.jobTitle) return null

  return (
    <Container id="recomended-courses">
      <Header>
        <Title>{i18n.t(`${TRANSLATION}.recommended.my_profile_courses_title`)}</Title>
        <LinkButton to="#" onClick={() => redirectToWebo()}>
          {i18n.t(`${TRANSLATION}.recommended.button`)} <Icon24.ChevronRight />
        </LinkButton>
      </Header>
      <CoursesList
        courses={data?.recommended}
        saved={data?.progression.saved ?? []}
        redirectToWebo={redirectToWebo}
      />
      <SnackBar />
    </Container>
  )
}

export { RecomendedCourses, JobRecomendedCourses, MyProfileRecomendedCourses }
export default RecomendedCourses
